import React, { forwardRef, useId } from "react";

import { cn } from "src/shadcn/lib/utils";
import {
  RadioGroup as PrimitiveRadioGroup,
  RadioGroupItem as PrimitiveRadioGroupItem,
} from "src/primitives/RadioGroup";
import { Label } from "src/primitives/Label";
import {
  Field,
  FieldLabel,
  FieldContent,
  FieldDescription,
  FieldError,
} from "src/primitives/Field";

export interface RadioGroupProps extends Omit<
  React.ComponentProps<typeof PrimitiveRadioGroup>,
  "children"
> {
  /** Group label displayed above the radio items. */
  label?: string;
  /** Error message displayed below the group. */
  error?: string;
  /** Helper text displayed below the group label. Accepts string or ReactNode. */
  helpText?: React.ReactNode;
  /** Layout orientation. Defaults to "horizontal". */
  orientation?: "horizontal" | "vertical";
  /** Additional class name for the outermost wrapper. */
  className?: string;
  /** RadioGroup.Item children. */
  children?: React.ReactNode;
  /** Props forwarded to the group Label element. */
  labelProps?: React.ComponentProps<typeof FieldLabel>;
}

export interface RadioGroupItemProps extends React.ComponentProps<
  typeof PrimitiveRadioGroupItem
> {
  /** Label displayed next to the radio. */
  label?: string;
  /** Helper text displayed below the item label. Accepts string or ReactNode. */
  helpText?: React.ReactNode;
  /** Radio value (required). */
  value: string;
  /** Additional class name for the item wrapper. */
  className?: string;
  /** Optional id override; auto-generated if omitted. */
  id?: string;
}

const RadioGroupItem = forwardRef<
  React.ComponentRef<typeof PrimitiveRadioGroupItem>,
  RadioGroupItemProps
>(({ label, helpText, value, className, id: idProp, ...props }, ref) => {
  const generatedId = useId();
  const id = idProp ?? generatedId;
  const helpTextId = `helpText_${id}`;

  return (
    <div
      className={cn(
        "flex gap-2",
        helpText ? "items-start" : "items-center",
        className
      )}
    >
      <PrimitiveRadioGroupItem
        ref={ref}
        id={id}
        value={value}
        aria-describedby={helpText ? helpTextId : undefined}
        className={helpText ? "mt-0.5" : undefined}
        {...props}
      />
      {(label || helpText) && (
        <div className="flex flex-col gap-0.5">
          {label && (
            <Label htmlFor={id} className="cursor-pointer">
              {label}
            </Label>
          )}
          {helpText && (
            <FieldDescription id={helpTextId}>{helpText}</FieldDescription>
          )}
        </div>
      )}
    </div>
  );
});

RadioGroupItem.displayName = "RadioGroup.Item";

const RadioGroupRoot = forwardRef<
  React.ComponentRef<typeof PrimitiveRadioGroup>,
  RadioGroupProps
>(
  (
    {
      label,
      error,
      helpText,
      orientation = "horizontal",
      className,
      disabled,
      required,
      children,
      labelProps,
      ...props
    },
    ref
  ) => {
    const generatedId = useId();
    const errorId = `error_${generatedId}`;
    const helpTextId = `helpText_${generatedId}`;

    const hasField = !!(label || error || helpText);

    const radioGroup = (
      <PrimitiveRadioGroup
        ref={ref}
        disabled={disabled}
        required={required}
        aria-invalid={!!error || undefined}
        aria-describedby={
          [error ? errorId : null, helpText ? helpTextId : null]
            .filter(Boolean)
            .join(" ") || undefined
        }
        orientation={orientation}
        className={cn(
          "data-[orientation=vertical]:grid data-[orientation=vertical]:gap-2 data-[orientation=horizontal]:flex data-[orientation=horizontal]:flex-row data-[orientation=horizontal]:flex-wrap data-[orientation=horizontal]:items-center data-[orientation=horizontal]:gap-4",
          !hasField && className
        )}
        {...props}
      >
        {children}
      </PrimitiveRadioGroup>
    );

    if (!hasField) {
      return radioGroup;
    }

    return (
      <Field
        data-disabled={disabled || undefined}
        data-invalid={!!error || undefined}
        className={className}
      >
        {(label || helpText) && (
          <div>
            {label && (
              <FieldLabel {...labelProps}>
                {label}
                {required && (
                  <span aria-hidden="true" className="text-destructive">
                    *
                  </span>
                )}
              </FieldLabel>
            )}
            {helpText && (
              <FieldDescription id={helpTextId}>{helpText}</FieldDescription>
            )}
          </div>
        )}
        <FieldContent>
          {radioGroup}
          {!!error && <FieldError id={errorId}>{error}</FieldError>}
        </FieldContent>
      </Field>
    );
  }
);

RadioGroupRoot.displayName = "RadioGroup";

const RadioGroup = Object.assign(RadioGroupRoot, {
  Item: RadioGroupItem,
});

export { RadioGroup };
